import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CarFront, 
  ClipboardCheck, 
  BadgeCheck, 
  TrendingDown, 
  CalendarX, 
  Camera,
  ChevronDown,
  ChevronUp,
  ArrowRight
} from 'lucide-react';
import { services, contactData } from '../data/mock';

const ServicesPage = () => {
  const [expandedService, setExpandedService] = useState(null);

  const getServiceIcon = (iconName) => {
    const icons = {
      'car-crash': CarFront,
      'clipboard-check': ClipboardCheck,
      'badge-check': BadgeCheck,
      'trending-down': TrendingDown,
      'calendar-x': CalendarX,
      'camera': Camera
    };
    return icons[iconName] || CarFront;
  };

  const serviceDetails = {
    1: 'Detaillierte Dokumentation aller Unfallschäden mit Fotodokumentation, Schadensbeschreibung und rechtssicherer Aufbereitung für Versicherung und Anwalt.',
    2: 'Objektive Bewertung von Fahrzeugschäden mit präziser Kostenermittlung für Reparatur oder Wiederbeschaffung.',
    3: 'Marktgerechte Wertermittlung für Kauf, Verkauf, Versicherung oder Finanzierung Ihres Fahrzeugs.',
    4: 'Professionelle Ermittlung der merkantilen Wertminderung nach Unfallschäden gemäß aktueller Rechtsprechung.',
    5: 'Berechnung des Nutzungsausfalls während der Reparaturzeit basierend auf Fahrzeugklasse und Nutzung.',
    6: 'Schnelle Sicherung aller unfallrelevanten Beweise, Spuren und Schäden direkt nach dem Unfall.'
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}`, '_blank');
  };

  return (
    <div style={{ background: '#000000', position: 'relative' }}>
      <div className="pattern-overlay" />
      
      {/* Hero */}
      <section style={{ paddingTop: '140px', paddingBottom: '80px', position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <h1 className="display-huge" style={{ 
              marginBottom: '24px',
              animation: 'fade-in-up 0.8s ease-out'
            }}>
              Dienstleistungen für <span style={{ color: '#00A8CC' }}>Unfall, Schaden & Bewertung</span>
            </h1>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '40px',
              animation: 'fade-in-up 0.8s ease-out 0.2s both'
            }}>
              Von der Beweissicherung bis zur vollständigen Gutachtenerstellung.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = getServiceIcon(service.icon);
              const isExpanded = expandedService === service.id;
              
              return (
                <div 
                  key={service.id}
                  className="glass-card"
                  style={{
                    cursor: 'pointer',
                    animation: `fade-in-up 0.6s ease-out ${index * 0.1}s both`
                  }}
                  onClick={() => setExpandedService(isExpanded ? null : service.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '12px',
                      background: 'rgba(0, 168, 204, 0.1)',
                      border: '1px solid rgba(0, 168, 204, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon size={28} style={{ color: '#00A8CC' }} />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                          {service.title}
                        </h3>
                        {isExpanded ? 
                          <ChevronUp size={24} style={{ color: '#00A8CC' }} /> : 
                          <ChevronDown size={24} style={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                        }
                      </div>
                      
                      <p className="body-medium" style={{ 
                        color: 'rgba(255, 255, 255, 0.6)',
                        marginBottom: isExpanded ? '16px' : '0'
                      }}>
                        {service.description}
                      </p>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div style={{
                          paddingTop: '16px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                          animation: 'fade-in-up 0.4s ease-out'
                        }}>
                          <p className="body-medium" style={{ 
                            color: 'rgba(255, 255, 255, 0.7)',
                            lineHeight: '1.7'
                          }}>
                            {serviceDetails[service.id]}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ablauf Timeline */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="display-large" style={{ marginBottom: '16px' }}>
              So läuft die Begutachtung ab
            </h2>
            <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Einfach, schnell und transparent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { num: 1, title: 'Anfrage', desc: 'Per Telefon, WhatsApp oder Formular' },
              { num: 2, title: 'Termin', desc: 'Vor-Ort Begutachtung vereinbaren' },
              { num: 3, title: 'Erstellung', desc: 'Gutachten wird sorgfältig erstellt' },
              { num: 4, title: 'Übergabe', desc: 'Rechtssichere Nutzung für Versicherung' }
            ].map((step, index) => (
              <div 
                key={step.num}
                style={{
                  textAlign: 'center',
                  animation: `fade-in-up 0.6s ease-out ${index * 0.15}s both`
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(0, 168, 204, 0.1)',
                  border: '2px solid rgba(0, 168, 204, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#00A8CC',
                  margin: '0 auto 20px'
                }}>
                  {step.num}
                </div>
                <h3 className="heading-2" style={{ marginBottom: '8px' }}>
                  {step.title}
                </h3>
                <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
          <div className="glass-card" style={{
            textAlign: 'center',
            padding: '60px 40px',
            background: 'rgba(0, 168, 204, 0.05)',
            border: '1px solid rgba(0, 168, 204, 0.2)'
          }}>
            <h2 className="display-large" style={{ marginBottom: '20px' }}>
              Sofort prüfen lassen
            </h2>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto 40px'
            }}>
              Kontaktieren Sie uns jetzt für Ihr Gutachten.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary">
                Jetzt anfragen
                <ArrowRight size={20} />
              </Link>
              <button onClick={handleWhatsApp} className="btn-secondary">
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;